/**
 * Phase 9.6 — Content Calendar (minimal list + create + AI generate).
 * Calendar grid + side-drawer + markdown live-preview deferred to 9.6.1.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, auth } from "../../lib/api";
import { toast } from "sonner";
import { showComplianceError }
  from "../../components/app/ComplianceErrorToast";
import { showAgentError } from "../../components/app/AgentErrorToast";
import { useLocale } from "../../lib/i18n";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle }
  from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue }
  from "../../components/ui/select";

const CHANNEL_COLORS = {
  linkedin: "bg-sky-100 text-sky-900",
  instagram: "bg-fuchsia-100 text-fuchsia-900",
  facebook: "bg-blue-100 text-blue-900",
  tiktok: "bg-stone-900 text-stone-50",
  youtube_shorts: "bg-rose-100 text-rose-900",
  blog: "bg-amber-100 text-amber-900",
};
const STATUS_COLORS = {
  draft: "bg-stone-100 text-stone-900",
  pending_approval: "bg-amber-100 text-amber-900",
  approved: "bg-emerald-100 text-emerald-900",
  scheduled: "bg-sky-100 text-sky-900",
  publishing: "bg-violet-100 text-violet-900",
  published: "bg-emerald-200 text-emerald-900",
  failed: "bg-rose-100 text-rose-900",
  archived: "bg-stone-50 text-stone-500",
};
const CHANNEL_LIMITS = {
  linkedin: 3000, instagram: 2200, facebook: 63206,
  tiktok: 2200, youtube_shorts: 1000, blog: 100000,
};
const WRITE_ROLES = new Set(["owner", "admin", "manager", "producer"]);
const APPROVE_ROLES = new Set(["owner", "admin", "manager", "approver"]);

export function ContentCalendar() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const user = auth.getUser ? auth.getUser() : { role: "owner" };
  const canWrite = WRITE_ROLES.has(user?.role);
  const canApprove = APPROVE_ROLES.has(user?.role);
  const navigate = useNavigate();
  const { locale } = useLocale();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/content/posts");
      setItems(r.data.items || []);
    } catch (e) { toast.error("Failed to load posts"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Phase 9.4.2 — Publish-now action with compliance-aware toast.
  const publishNow = async (post) => {
    try {
      await api.post(`/content/posts/${post.id}/publish-now`);
      toast.success("Published");
      load();
    } catch (err) {
      showComplianceError({ err, locale, navigate, role: user?.role });
    }
  };

  return (
    <div className="space-y-4" data-testid="content-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Content Calendar</h1>
          <p className="text-sm text-muted-foreground">
            Organic posts across channels. Publishing is mocked.
          </p>
        </div>
        {canWrite && (
          <Button data-testid="content-new-btn"
                  onClick={() => setShowCreate(true)}>
            New post
          </Button>
        )}
      </div>
      {loading ? <p>Loading…</p> :
        items.length === 0 ? (
          <Card><CardContent className="py-8 text-center text-muted-foreground"
                                  data-testid="content-empty">
            No posts yet.
          </CardContent></Card>
        ) : (
          <div className="space-y-2">
            {items.map(p => (
              <Card key={p.id} data-testid={`content-row-${p.id}`}>
                <CardContent className="p-3 flex items-center gap-3 flex-wrap">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${CHANNEL_COLORS[p.channel] || "bg-stone-100"}`}
                          data-testid={`content-channel-${p.id}`}>
                    {p.channel}
                  </span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_COLORS[p.status] || "bg-stone-100"}`}
                          data-testid={`content-status-${p.id}`}>
                    {p.status}
                  </span>
                  <span className="flex-1 truncate font-medium">{p.title || "(untitled)"}</span>
                  {p.scheduled_at && (
                    <span className="text-xs text-muted-foreground"
                            data-testid={`content-scheduled-${p.id}`}>
                      {new Date(p.scheduled_at).toLocaleString()}
                    </span>
                  )}
                  {p.external_post_id && (
                    <Badge variant="outline" className="font-mono text-[10px]">
                      {p.external_post_id}
                    </Badge>
                  )}
                  {canApprove && ["approved", "scheduled"].includes(p.status) && (
                    <Button size="sm" variant="outline"
                            data-testid={`content-publish-now-${p.id}`}
                            onClick={() => publishNow(p)}>
                      Publish now
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      {showCreate && (
        <CreatePostModal onClose={() => setShowCreate(false)}
                              onCreated={() => { setShowCreate(false); load(); }} />
      )}
    </div>
  );
}

function CreatePostModal({ onClose, onCreated }) {
  const [channel, setChannel] = useState("linkedin");
  const [postType, setPostType] = useState("organic_post");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [created, setCreated] = useState(null);

  const limit = CHANNEL_LIMITS[channel] || 100000;
  const overLimit = body.length > limit;
  const nearLimit = body.length > limit * 0.95 && !overLimit;
  const valid = title.trim() && body.trim() && channel && !overLimit;

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post("/content/posts", {
        channel, post_type: postType, title, body_md: body });
      setCreated(r.data);
      toast.success("Post created");
      onCreated();
    } catch (e) {
      toast.error("Failed to create post");
    } finally { setBusy(false); }
  };

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Enter a prompt first"); return; }
    setGenerating(true);
    try {
      // Two-step: create empty draft, generate into it, then read back.
      const draft = await api.post("/content/posts", {
        channel, post_type: postType,
        title: "AI draft", body_md: "tbd" });
      const r = await api.post(
        `/content/posts/${draft.data.id}/generate`,
        { prompt, channel, post_type: postType });
      setTitle(r.data.title || "");
      setBody(r.data.body_md || "");
      toast.success("AI draft populated");
      onCreated();
    } catch (e) { showAgentError({ err: e, locale }); }
    finally { setGenerating(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="content-create-modal" className="max-w-2xl">
        <DialogHeader><DialogTitle>New post</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Channel</Label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger data-testid="content-channel-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {Object.keys(CHANNEL_LIMITS).map(c =>
                    <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Post type</Label>
              <Select value={postType} onValueChange={setPostType}>
                <SelectTrigger data-testid="content-post-type-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="organic_post">Organic post</SelectItem>
                  <SelectItem value="thought_leadership">Thought leadership</SelectItem>
                  <SelectItem value="local_market_update">Local market update</SelectItem>
                  <SelectItem value="recruiting">Recruiting</SelectItem>
                  <SelectItem value="case_study">Case study</SelectItem>
                  <SelectItem value="success_story">Success story</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)}
                    data-testid="content-title-input" />
          </div>
          <div>
            <Label>Body (markdown)</Label>
            <Textarea rows={8} value={body}
                          onChange={e => setBody(e.target.value)}
                          data-testid="content-body-input" />
            <p className={`text-xs mt-1 ${overLimit ? "text-rose-600" : nearLimit ? "text-amber-600" : "text-muted-foreground"}`}
                data-testid="content-body-counter">
              {body.length} / {limit}
            </p>
          </div>
          <div className="border-t pt-3 space-y-2">
            <Label>Generate with AI</Label>
            <Input value={prompt} onChange={e => setPrompt(e.target.value)}
                    placeholder="e.g. Marktbericht Wuppertal Q1"
                    data-testid="content-ai-prompt" />
            <Button variant="outline" onClick={generate}
                    disabled={generating}
                    data-testid="content-ai-generate-btn">
              {generating ? "Generating…" : "Generate draft"}
            </Button>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button onClick={save} disabled={!valid || busy}
                    data-testid="content-save-btn">
              {busy ? "Saving…" : "Create draft"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
